"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Archive,
  Copy,
  Download,
  Edit3,
  FolderTree,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Rocket,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { saveDownload } from "@/lib/download";
import {
  exportErrorMessage,
  exportFormatLabel,
  exportSuccessMessage,
  type ExportFormat,
} from "@/lib/export-format";
import {
  CatalogueTemplateService,
  type CatalogueTemplateStatus,
  type CategoryTemplate,
  type CategoryTemplateCategory,
  type CategoryTemplateDetail,
} from "@/services/catalogue-template.service";
import { ExportFormatMenuItems } from "../export-format-menu";
import { ConfigurationTransferDialog, type TransferMode } from "../templates/configuration-transfer-dialog";
import { TemplateSelector } from "../templates/template-selector";
import { CategoryTemplateAttributeEditor } from "./category-template-attribute-editor";
import { CategoryTemplateTree } from "./category-template-tree";
import { LanguageSelector } from "@/components/catalog/language-selector";
import { localizedText } from "@/lib/categories/localization";
import type { LanguageCode } from "@/constants/languages";

type TemplateDialogState =
  | { mode: "create" }
  | { mode: "edit"; template: CategoryTemplateDetail }
  | { mode: "clone"; template: CategoryTemplate }
  | null;

interface TemplateFormState {
  name: string;
  version: string;
  description: string;
  isDefault: boolean;
  languages: LanguageCode[];
}

const emptyTemplateForm: TemplateFormState = {
  name: "",
  version: "",
  description: "",
  isDefault: false,
  languages: ["en"],
};

const statusStyles: Record<CatalogueTemplateStatus, string> = {
  draft: "border-blue-200 bg-blue-50 text-blue-700",
  published: "border-green-200 bg-green-50 text-green-700",
  archived: "border-slate-200 bg-slate-50 text-slate-600",
};

function formatTimestamp(value?: Date | null): string {
  if (!value || Number.isNaN(value.getTime())) return "Not set";
  return value.toLocaleString();
}

/** Short enough to sit inline beside the picker, and shown only when it applies. */
function readOnlyMessage(status: CatalogueTemplateStatus): string | null {
  if (status === "published") return "Read-only — clone to a draft to edit";
  if (status === "archived") return "Read-only — archived";
  return null;
}

function findCategory(
  categories: CategoryTemplateCategory[],
  categoryId: string | null
): CategoryTemplateCategory | null {
  if (!categoryId) return null;
  return categories.find((category) => category.id === categoryId) ?? null;
}

export function CategoryTemplateManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTemplateIdRef = useRef(searchParams.get("template"));
  const [templates, setTemplates] = useState<CategoryTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateIdState] = useState<string | null>(null);
  const [detail, setDetail] = useState<CategoryTemplateDetail | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [dialog, setDialog] = useState<TemplateDialogState>(null);
  const [form, setForm] = useState<TemplateFormState>(emptyTemplateForm);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [transferMode, setTransferMode] = useState<TransferMode | null>(null);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const detailRequestIdRef = useRef(0);
  const selectedTemplateIdRef = useRef<string | null>(null);

  const selectTemplate = useCallback((templateId: string | null) => {
    selectedTemplateIdRef.current = templateId;
    setSelectedTemplateIdState(templateId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (templateId) next.set("template", templateId);
      else next.delete("template");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const loadTemplates = useCallback(async (preferredTemplateId?: string) => {
    setListLoading(true);
    setError(null);
    try {
      const data = await CatalogueTemplateService.listCategoryTemplates();
      setTemplates(data);
      const next = preferredTemplateId ?? selectedTemplateIdRef.current ?? initialTemplateIdRef.current;
      selectTemplate(next && data.some((template) => template.id === next) ? next : data[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load category templates");
    } finally {
      setListLoading(false);
    }
  }, [selectTemplate]);

  const loadDetail = useCallback(async (templateId: string) => {
    if (selectedTemplateIdRef.current !== templateId) return;

    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    const isCurrentRequest = () =>
      detailRequestIdRef.current === requestId && selectedTemplateIdRef.current === templateId;

    setDetailLoading(true);
    setError(null);
    try {
      const data = await CatalogueTemplateService.getCategoryTemplate(templateId);
      if (!isCurrentRequest()) return;
      setDetail(data);
      setSelectedCategoryId((current) =>
        current && data.categories.some((category) => category.id === current) ? current : null
      );
    } catch (err) {
      if (!isCurrentRequest()) return;
      setDetail(null);
      setError(err instanceof Error ? err.message : "Failed to load category template detail");
    } finally {
      if (isCurrentRequest()) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (selectedTemplateId) {
      loadDetail(selectedTemplateId);
    } else {
      selectedTemplateIdRef.current = null;
      detailRequestIdRef.current += 1;
      setDetail(null);
      setDetailLoading(false);
    }
  }, [loadDetail, selectedTemplateId]);

  const refreshSelected = () => {
    const templateId = selectedTemplateIdRef.current;
    if (!templateId) return;
    loadDetail(templateId);
  };

  const updateForm = (patch: Partial<TemplateFormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const openCreate = () => {
    setForm(emptyTemplateForm);
    setDialogError(null);
    setDialog({ mode: "create" });
  };

  const openEdit = (template: CategoryTemplateDetail) => {
    setForm({
      name: template.name,
      version: template.version,
      description: template.description ?? "",
      isDefault: template.isDefault,
      languages: template.languages as LanguageCode[],
    });
    setDialogError(null);
    setDialog({ mode: "edit", template });
  };

  const openClone = (template: CategoryTemplate) => {
    setForm({
      name: `${template.name} draft`,
      version: `${template.version}-draft`,
      description: template.description ?? "",
      isDefault: false,
      languages: template.languages as LanguageCode[],
    });
    setDialogError(null);
    setDialog({ mode: "clone", template });
  };

  const submitDialog = async () => {
    if (!dialog) return;

    const name = form.name.trim();
    const version = form.version.trim();
    if (!name || (dialog.mode !== "edit" && !version)) {
      setDialogError("Name and version are required");
      return;
    }

    setDialogBusy(true);
    setDialogError(null);
    setMessage(null);
    const description = form.description.trim() || undefined;

    try {
      if (dialog.mode === "create") {
        const created = await CatalogueTemplateService.createCategoryTemplate({
          name,
          version,
          description,
          is_default: form.isDefault,
          languages: form.languages,
        });
        await loadTemplates(created.id);
        selectTemplate(created.id);
        setMessage({ type: "ok", text: "Draft category template created." });
      } else if (dialog.mode === "edit") {
        const updated = await CatalogueTemplateService.updateCategoryTemplate(dialog.template.id, {
          name,
          description: form.description.trim() ? form.description.trim() : null,
          is_default: form.isDefault,
        });
        await loadTemplates(updated.id);
        await loadDetail(updated.id);
        setMessage({ type: "ok", text: "Template details saved." });
      } else {
        const clone = await CatalogueTemplateService.cloneCategoryTemplate(dialog.template.id, {
          name,
          version,
          description,
          is_default: form.isDefault,
        });
        await loadTemplates(clone.id);
        selectTemplate(clone.id);
        setMessage({ type: "ok", text: "Template cloned to a draft." });
      }
      setDialog(null);
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : "Template save failed");
    } finally {
      setDialogBusy(false);
    }
  };

  const publishTemplate = async () => {
    if (!detail) return;
    if (!window.confirm(`Publish "${detail.name}"? Published templates are read-only.`)) return;

    setMessage(null);
    try {
      const published = await CatalogueTemplateService.publishCategoryTemplate(detail.id, {
        is_default: detail.isDefault,
      });
      await loadTemplates(published.id);
      await loadDetail(published.id);
      setMessage({ type: "ok", text: "Template published." });
    } catch (err) {
      setMessage({ type: "err", text: err instanceof Error ? err.message : "Publish failed" });
    }
  };

  const archiveTemplate = async () => {
    if (!detail) return;
    if (!window.confirm(`Archive "${detail.name}"? Archived templates cannot be edited.`)) return;

    setMessage(null);
    try {
      const archived = await CatalogueTemplateService.archiveCategoryTemplate(detail.id);
      await loadTemplates(archived.id);
      await loadDetail(archived.id);
      setMessage({ type: "ok", text: "Template archived." });
    } catch (err) {
      setMessage({ type: "err", text: err instanceof Error ? err.message : "Archive failed" });
    }
  };

  const exportTemplate = async (format: ExportFormat) => {
    if (!detail) return;

    setExportingFormat(format);
    setMessage(null);
    try {
      saveDownload(await CatalogueTemplateService.exportCategoryTemplate(detail.id, format));
      setMessage({
        type: "ok",
        text: exportSuccessMessage("Category configuration", format),
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: exportErrorMessage("Category configuration", format, err),
      });
    } finally {
      setExportingFormat(null);
    }
  };

  const readOnly = detail ? detail.status !== "draft" : true;
  const selectedCategory = detail ? findCategory(detail.categories, selectedCategoryId) : null;
  const templateLanguages = Array.isArray(detail?.languages)
    ? detail.languages
    : [];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="mb-5 flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-center sm:justify-between">
            <TemplateSelector
              templates={templates}
              selectedId={selectedTemplateId}
              loading={listLoading}
              onSelect={selectTemplate}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => loadTemplates()}
                disabled={listLoading}
                aria-label="Refresh category configurations"
              >
                <RefreshCw className={cn("h-4 w-4", listLoading && "animate-spin")} />
              </Button>
              <Button type="button" size="sm" onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" /> New draft
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="icon" aria-label="Configuration actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem disabled={!detail} onSelect={() => detail && openClone(detail)}>
                    <Copy /> Clone to draft
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!detail || readOnly} onSelect={() => detail && openEdit(detail)}>
                    <Edit3 /> Edit details
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!detail || detail.status !== "draft"} onSelect={() => void publishTemplate()}>
                    <Rocket /> Publish
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!detail || detail.status === "archived"} onSelect={() => void archiveTemplate()} className="text-red-600">
                    <Archive /> Archive
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      disabled={!detail || detail.status === "archived" || Boolean(exportingFormat)}
                    >
                      {exportingFormat ? <Loader2 className="animate-spin" /> : <Download />}
                      {exportingFormat
                        ? `Exporting ${exportFormatLabel(exportingFormat)}…`
                        : "Export"}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent aria-label="Category configuration export format">
                      <ExportFormatMenuItems
                        disabled={Boolean(exportingFormat)}
                        onSelect={(format) => void exportTemplate(format)}
                      />
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem onSelect={() => setTransferMode("import")}>
                    <Upload /> Import
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

            {error && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {message && (
              <div
                role={message.type === "ok" ? "status" : "alert"}
                className={cn(
                  "mb-4 rounded-md border p-3 text-sm",
                  message.type === "ok"
                    ? "border-green-200 bg-green-50 text-green-700"
                    : "border-red-200 bg-red-50 text-red-700"
                )}
              >
                {message.text}
              </div>
            )}

            {!selectedTemplateId ? (
              <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground">
                <FolderTree className="h-8 w-8 mb-2" />
                <p className="text-sm">Select or create a category template.</p>
              </div>
            ) : detailLoading || !detail ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading template...
              </div>
            ) : (
              <div className="space-y-5">
                {/* One line of state. The picker above already names the
                    template and its version, so neither is repeated here. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                  <Badge
                    className={cn("capitalize", statusStyles[detail.status])}
                    variant="outline"
                  >
                    {detail.status}
                  </Badge>
                  {detail.isDefault && <Badge variant="secondary">Default</Badge>}
                  <span className="text-muted-foreground">
                    <strong className="font-semibold text-foreground">
                      {detail.categories.length}
                    </strong>{" "}
                    categories ·{" "}
                    <strong className="font-semibold text-foreground">
                      {detail.attributes.length}
                    </strong>{" "}
                    attributes
                  </span>
                  <span
                    className="text-xs text-muted-foreground"
                    title={
                      detail.description
                        ? `${detail.description}\nUpdated ${formatTimestamp(detail.updatedAt)}`
                        : `Updated ${formatTimestamp(detail.updatedAt)}`
                    }
                  >
                    Updated {formatTimestamp(detail.updatedAt)}
                  </span>
                  {readOnlyMessage(detail.status) && (
                    <span className="ml-auto rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                      {readOnlyMessage(detail.status)}
                    </span>
                  )}
                </div>

                {templateLanguages.length === 0 ? (
                  <div
                    role="alert"
                    className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                  >
                    This template has no configured languages. Repair its language
                    configuration before editing categories or attributes.
                  </div>
                ) : (
                  <div className="flex items-stretch overflow-hidden rounded-lg border [height:clamp(520px,calc(100vh-24rem),840px)]">
                    <CategoryTemplateTree
                      templateId={detail.id}
                      categories={detail.categories}
                      languages={templateLanguages}
                      readOnly={readOnly}
                      selectedCategoryId={selectedCategoryId}
                      onSelectCategory={setSelectedCategoryId}
                      onChanged={refreshSelected}
                    />
                    <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4">
                      <CategoryTemplateAttributeEditor
                        templateId={detail.id}
                        categoryId={selectedCategoryId}
                        categoryName={
                          selectedCategory
                            ? `${localizedText(selectedCategory.name)} attributes`
                            : "General default attributes"
                        }
                        languages={templateLanguages}
                        attributes={detail.attributes}
                        readOnly={readOnly}
                        onChanged={refreshSelected}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "create"
                ? "Create category template"
                : dialog?.mode === "clone"
                  ? "Clone template to draft"
                  : "Edit template details"}
            </DialogTitle>
            <DialogDescription>
              {dialog?.mode === "clone"
                ? "Clones always create a new draft that can be edited before publishing."
                : "Category configurations are scoped to the current client."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="category-template-name">Name</Label>
              <Input
                id="category-template-name"
                value={form.name}
                onChange={(event) => updateForm({ name: event.target.value })}
                placeholder="Default furniture categories"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category-template-version">Version</Label>
              <Input
                id="category-template-version"
                value={form.version}
                onChange={(event) => updateForm({ version: event.target.value })}
                placeholder="v1"
                disabled={dialog?.mode === "edit"}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category-template-description">Description</Label>
              <Textarea
                id="category-template-description"
                value={form.description}
                onChange={(event) => updateForm({ description: event.target.value })}
                placeholder="Optional notes for this template"
              />
            </div>
            {dialog?.mode === "create" ? (
              <LanguageSelector
                value={form.languages}
                onChange={(languages) => updateForm({ languages })}
                idPrefix="template-language"
              />
            ) : (
              <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                Languages are immutable: {form.languages.join(", ")}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                id="category-template-default"
                checked={form.isDefault}
                onCheckedChange={(checked) => updateForm({ isDefault: checked })}
              />
              <Label htmlFor="category-template-default" className="text-sm">
                Mark as default when published
              </Label>
            </div>
            {dialogError && <p className="text-sm text-red-600">{dialogError}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialog(null)} disabled={dialogBusy}>
              Cancel
            </Button>
            <Button type="button" onClick={submitDialog} disabled={dialogBusy}>
              {dialogBusy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {dialog?.mode === "clone" ? "Clone" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfigurationTransferDialog
        open={transferMode !== null}
        mode={transferMode ?? "export"}
        selected={detail}
        templates={templates}
        onOpenChange={(open) => !open && setTransferMode(null)}
        onExport={() => {
          if (!detail) throw new Error("Select a configuration to export");
          return CatalogueTemplateService.exportCategoryTemplate(detail.id, "json");
        }}
        onPreview={CatalogueTemplateService.previewCategoryTemplateImport}
        onImport={CatalogueTemplateService.importCategoryTemplate}
        onImported={async (templateId) => {
          await loadTemplates(templateId);
          selectTemplate(templateId);
          setMessage({ type: "ok", text: "Category configuration imported into a draft." });
        }}
      />
    </div>
  );
}
